import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { GET_IPS_VIOLATION_POINTS, GET_VIOLATION_TYPES, GET_ANALYTES_AND_GROUPS } from "../../queries/ipsAdminQrys";
import "./IPSViolationAdmin.css";
import {
  Table,
  TableBody,
  TableBodyData,
  TableBodyDataCellTemplate,
  TableBodyDataRowTemplate,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TablePagination,
  TableWrapper,
  TableFilterTextInput,
  TableFilters,
  TableSortingRules,
  TableSortingRule,
  TextInput,
  Button,
  Modal,
  ModalTitle,
  ModalContent,
  ModalFooter,
  Select,
  SelectOption
} from '@utahdts/utah-design-system';

import { ComboBox } from '../archive/utah-design-system/react/components/forms/ComboBox/ComboBox';
import { ComboBoxOption } from '../archive/utah-design-system/react/components/forms/ComboBox/ComboBoxOption.jsx';

const IPSViolationAdmin = () => {

  const user = sessionStorage.getItem("user")
    ? JSON.parse(sessionStorage.user)
    : JSON.parse(sessionStorage.defaultUser);
  const { data: ipsViolationPointsData, error: ipsViolationPointsError } = useQuery(GET_IPS_VIOLATION_POINTS);
  const { data: violationTypesData } = useQuery(GET_VIOLATION_TYPES);
  const { data: analytesAndGroupsData } = useQuery(GET_ANALYTES_AND_GROUPS);
  const [tableData, setTableData] = useState([]);
  const [showCreateIPSViolationModal, setShowCreateIPSViolationModal] = useState(false);
  const [formattedViolationTypes, setFormattedViolationTypes] = useState([]);
  const [formattedAnalytesAndGroups, setFormattedAnalytesAndGroups] = useState([]);
  const [selectedSdwisViolationType, setSelectedSdwisViolationType] = useState(null);
  const [selectedAnalyteOrGroup, setSelectedAnalyteOrGroup] = useState(null);
  const [selectedIpsPoints, setSelectedIpsPoints] = useState('');

  useEffect(() => {
    if (ipsViolationPointsData?.getIPSViolationPoints) {
      const formattedData = ipsViolationPointsData.getIPSViolationPoints.map((item) => ({
        ...item,
        violationTypeDisplay: `${item.violationType.type} ${item.violationType.name}`.trim(),
        analyteName: item.analyte?.name || '',
        ipsPoints: String(item.ipsPoints),
      }));
      setTableData(formattedData);
    }
  }, [ipsViolationPointsData]);

  useEffect(() => {
    if (violationTypesData?.getAllViolationTypes) {
      const formattedViolationTypes = violationTypesData.getAllViolationTypes.map((type) => ({
        value: `${type.type} ${type.name}`.trim(),
        label: `${type.type} ${type.name}`.trim(),
      }));
      setFormattedViolationTypes(formattedViolationTypes);
    }
  }, [violationTypesData]);

  useEffect(() => {
    if (analytesAndGroupsData?.getAllAnalyteGroupsAndAnalytes) {
      const { analyteGroups, analytes } = analytesAndGroupsData.getAllAnalyteGroupsAndAnalytes;

      const formattedGroups = (analyteGroups || []).map((group) => ({
        value: `${group.code} ${group.name}`.trim(),
        label: `${group.code} ${group.name}`.trim(),
      }));

      const formattedAnalytes = (analytes || []).map((analyte) => ({
        value: `${analyte.code} ${analyte.name}`.trim(),
        label: `${analyte.code} ${analyte.name}`.trim(),
      }));

      setFormattedAnalytesAndGroups([...formattedGroups, ...formattedAnalytes]);
    }
  }, [analytesAndGroupsData]);

  if (ipsViolationPointsError) {
    return (
      <div className="utah-design-system page-container">
        <h3>IPS Violations Admin</h3>
        <p>You do not have access to view this page.</p>
      </div>
    );
  }

  return (
    <div className="utah-design-system page-container">
      <h3>IPS Violations Admin</h3>
      <br />
      {user?.permissions?.includes("edit_ips_admin") && (
        <Button variant="primary" className="mb-spacing" onClick={() => setShowCreateIPSViolationModal(true)}>Add New</Button>
        )}
      
      {tableData.length > 0 && (
        <TableWrapper>
          <Table>
          <TableHead>
            <TableSortingRules>
              <TableSortingRule allyLabel="Sort by Violation Type" recordFieldPath="violationTypeDisplay" />
              <TableSortingRule allyLabel="Sort by Analyte/Analyte Group" recordFieldPath="analyteName" />
              <TableSortingRule allyLabel="Sort by Rule Reference" recordFieldPath="ruleReference" />
              <TableSortingRule allyLabel="Sort by Points" recordFieldPath="ipsPoints" />
              <TableSortingRule allyLabel="Sort by Return to Compliance Method" recordFieldPath="returnToComplianceMethod" />
              <TableSortingRule allyLabel="Sort by Last Updated" recordFieldPath="lastUpdatedDate" />
            </TableSortingRules>
            <TableHeadRow>
              <TableHeadCell recordFieldPath="violationTypeDisplay">Violation Type</TableHeadCell>
              <TableHeadCell recordFieldPath="analyteName">Analyte/Analyte Group</TableHeadCell>
              <TableHeadCell recordFieldPath="ruleReference">Rule Reference</TableHeadCell>
              <TableHeadCell recordFieldPath="ipsPoints">Points</TableHeadCell>
              <TableHeadCell recordFieldPath="returnToComplianceMethod">Return to Compliance Method</TableHeadCell>
              <TableHeadCell recordFieldPath="lastUpdatedDate">Last Updated</TableHeadCell>
              <TableHeadCell recordFieldPath="edit"></TableHeadCell>
            </TableHeadRow>
            <TableFilters>
              <TableFilterTextInput a11yLabel="Filter by Violation Type" recordFieldPath="violationTypeDisplay" placeholder={'filter'}/>
              <TableFilterTextInput a11yLabel="Filter by Analyte/Analyte Group" recordFieldPath="analyteName" placeholder={'filter'}/>
              <TableFilterTextInput a11yLabel="Filter by Rule Reference" recordFieldPath="ruleReference" placeholder={'filter'}/>
              <TableFilterTextInput a11yLabel="Filter by Points" recordFieldPath="ipsPoints" placeholder={'filter'}/>
              <TableFilterTextInput a11yLabel="Filter by Return to Compliance Method" recordFieldPath="returnToComplianceMethod" placeholder={'filter'}/>
              <TableFilterTextInput a11yLabel="Filter by Last Updated" recordFieldPath="lastUpdatedDate" placeholder={'filter'}/>
              <TableHeadCell></TableHeadCell>
            </TableFilters>
          </TableHead>
          <TableBody>
            <TableBodyData records={tableData} recordIdField="violationId">
              <TableBodyDataRowTemplate>
                <TableBodyDataCellTemplate recordFieldPath="violationTypeDisplay" />
                <TableBodyDataCellTemplate recordFieldPath="analyteName" />
                <TableBodyDataCellTemplate recordFieldPath="ruleReference" />
                <TableBodyDataCellTemplate recordFieldPath="ipsPoints" />
                <TableBodyDataCellTemplate recordFieldPath="returnToComplianceMethod" />
                <TableBodyDataCellTemplate recordFieldPath="lastUpdatedDate" />
                <TableBodyDataCellTemplate >Edit Delete</TableBodyDataCellTemplate>
              </TableBodyDataRowTemplate>
            </TableBodyData>
          </TableBody>
          </Table>
          <TablePagination
            className="mt-spacing"
            id="table-example-render-pagination-id"
            itemsPerPage={25}
          />
        </TableWrapper>
      )}

      {showCreateIPSViolationModal && (
        <Modal
          className="modal--large"
          onClose={() => setShowCreateIPSViolationModal(false)}
          onEscape={() => setShowCreateIPSViolationModal(false)}
        >
          <ModalTitle>New IPS Violation Points</ModalTitle>
          <ModalContent id="create-ips-violation-modal-content">
            <div className="ips-modal-grid">
              <ComboBox
                id="sdwis-violation-type-name-combobox"
                label="SDWIS Violation Type/Name"
                isClearable
                isRequired
                value={selectedSdwisViolationType}
                onChange={(value) => setSelectedSdwisViolationType(value)}
                onClear={() => setSelectedSdwisViolationType('')}
                autoComplete="off"
                portalTarget={document.body}
              >
                {formattedViolationTypes.map((option) => (
                  <ComboBoxOption key={option.value} value={option.value} label={option.label} />
                ))}
              </ComboBox>
              <TextInput
                isClearable
                isRequired
                label="IPS Report Violation Name"
              />
              <div className="full-width">
                <ComboBox
                  id="sdwis-analyte-combobox"
                  label="Analyte/Analyte Group"
                  isClearable
                  isRequired
                  autoComplete="off"
                  value={selectedAnalyteOrGroup}
                  onChange={(value) => setSelectedAnalyteOrGroup(value)}
                  onClear={() => setSelectedAnalyteOrGroup('')}
                  portalTarget={document.body}
                >
                  {formattedAnalytesAndGroups.map((option) => (
                    <ComboBoxOption key={option.value} value={option.value} label={option.label} />
                  ))}
                </ComboBox>
              </div>
              <TextInput
                isClearable
                isRequired
                label="Rule Reference"
              />
              <Select
                isRequired
                label="IPS Points"
                value={selectedIpsPoints}
                onChange={(e) => setSelectedIpsPoints(e.target.value)}
              >
                <SelectOption label="0" value={0} />
                <SelectOption label="15" value={15} />
                <SelectOption label="25" value={25} />
                <SelectOption label="50" value={50} />
                <SelectOption label="100" value={100} />
                <SelectOption label="150" value={150} />
                <SelectOption label="200" value={200} />
              </Select>
              <Select
                isClearable
                isRequired
                label="Return to Compliance Method"
              >
              </Select>
              <Select
                isClearable
                isRequired
                label="IPS Violation Types"
              >
              </Select>
            </div>
          </ModalContent>
          <ModalFooter>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button variant="secondary" onClick={() => setShowCreateIPSViolationModal(false)}>Cancel</Button>
            <Button variant="primary">Save</Button>
            </div>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

export default IPSViolationAdmin;